/* eslint-disable no-useless-computed-key */
import { useEffect } from "react";
import { createContext, ReactNode, useContext, useState } from "react";
import api from "../services/api";
import Swal from "sweetalert2";
import { Navigate } from "react-router";
import moment from "moment";
import { verificarDiaMes } from "utils/verificarDiaMes";

interface IProps {
  children: ReactNode;
}

type Contrato = [
  {
    id: number;
    desc_contrato: string;
    fl_copart: boolean;
    fl_ativo: boolean;
    dia_venc_copart: any;
    dt_ini_vigencia: any;
    dt_fim_vigencia: any;
    operadora: any;
    entidade: any;
  },
];

type User = {
  id: number;
  username: string;
  nome: string;
  email: string;
  role: {
    name: string;
  };
  operadora: {
    id: number;
    nm_operadora: string;
  };
  entidade: any;
  fl_primeiro_acesso: boolean;
  usuarios_contratoes: any;
  fl_medico: boolean;
};

type SignInData = {
  username: string;
  password: string;
};

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  contratos: Contrato | [];
  qtdContratosCobranca: any;
  qtdContratosCoparticipacao: any;
  qtdContratosRepasseTaxas: any;
  qtdContratosUtilizacao: any;
  setUser: (user: User) => void;
  signIn: (data: SignInData) => Promise<void>;
  signOut: () => void;
  signed: boolean;
  loadingLogin: boolean;
  userType: string;
};

const AuthContext = createContext({} as AuthContextType);

export function AuthProvider({ children }: IProps) {
  const [user, setUser] = useState<User | null>(null);
  const [contratos, setContratos] = useState<Contrato | []>([]);
  const [qtdContratosCobranca, setQtdContratosCobranca] = useState<any>(null);
  const [qtdContratosCoparticipacao, setQtdContratosCoparticipacao] =
    useState<any>(null);
  const [qtdContratosRepasseTaxas, setQtdContratosRepasseTaxas] =
    useState<any>(null);
  const [qtdContratosUtilizacao, setQtdContratosUtilizacao] =
    useState<any>(null);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [signed, setSigned] = useState(false);
  const [userType, setuserType] = useState("");

  const yearMoment = moment().year(); //2021
  const momentAddMonthYear = moment().add(1, "month").year(); // ano com 1 mês a frente
  const momentAddMonthMonthPlus = moment().add(1, "month").month() + 1; // numero de 2 meses a frente
  const momentAddMonthMonth = moment().add(1, "month").month(); // número de 1 mês a frente
  const momentMonthOnly = moment().month(); // número do Mês no moment
  const momentMonthPlus = moment().month() + 1; // número do mês atual

  const isAuthenticated = !!user;

  useEffect(() => {
    async function loadStorage() {
      const url_atual = window.location.href;
      if (url_atual.split("id=")[1]) {
        localStorage.setItem(
          "@portalinterbrasil:token",
          url_atual.split("id=")[1]?.replace("%22", "").replace("%22", ""),
        );
      }
      const storageToken = localStorage.getItem("@portalinterbrasil:token");

      async function recoverUserData() {
        try {
          await api.get("/users/me").then((res: any) => {
            setUser(res.data);
            setuserType(res.data.role.name);
            getContratos(res.data.id);
          });
        } catch (err) {
          <Navigate replace to="/" />;
        }
      }

      if (storageToken) {
        api.defaults.headers.common["Authorization"] = `Bearer ${storageToken}`;
        setSigned(true);
        recoverUserData();
        getContratosAllCobranca();
        getContratosAllRepasseTaxas();
        getContratosAllUtilizacao();
        getContratosAllCoparticipacao();
      }
    }
    loadStorage();
    // setLoadingLogin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getContratos(id: any) {
    try {
      const response = await api.get(
        `/usuarios-contratoes?users_permissions_user=${id}`,
      );
      if (response.data.length > 0) {
        setQtdContratosCobranca(
          response.data[0].contratoes.filter(
            (contrato: any) => contrato.fl_extrato_boleto_nota,
          ).length,
        );
        setQtdContratosRepasseTaxas(
          response.data[0].contratoes.filter(
            (contrato: any) => contrato.fl_repasse_taxa,
          ).length,
        );
        setQtdContratosUtilizacao(
          response.data[0].contratoes.filter(
            (contrato: any) => contrato.fl_sinistralidade,
          ).length,
        );
        setQtdContratosCoparticipacao(
          response.data[0].contratoes.filter(
            (contrato: any) => contrato.fl_copart,
          ).length,
        );
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Erro ao listar os contratos",
        showConfirmButton: true,
        confirmButtonText: `Ok`,
      });
    }
  }

  async function getContratosAllCobranca() {
    try {
      let data;

      const response = await api.get(`/contratoes`);
      data = response.data;

      if (data.length > 0) {
        setContratos(data);

        data
          .filter((doc: any) => doc.fl_extrato_boleto_nota && doc.fl_ativo)
          .filter((doc: any) => doc.fl_libera_valid_extbolnot_mes_anterior)
          .filter(
            (doc: any) =>
              moment([
                doc.ano_ini_vigenc_ext_bol_not,
                doc.mes_ini_vigenc_ext_bol_not - 1,
              ]).format("YYYY/MM") <=
              moment([moment().year(), moment().month()])
                .add(1, "M")
                .format("YYYY/MM"),
          )
          .filter(
            (doc: any) =>
              doc.documentos.find(
                (doc: any) =>
                  doc.tipo_documento === 1 &&
                  moment([doc.ano_competencia, doc.mes_competencia - 1]).format(
                    "YYYY/MM",
                  ) ===
                    moment([moment().year(), moment().month()])
                      .add(1, "M")
                      .format("YYYY/MM"),
              ) === undefined,
          )
          .map(async (doc: any) => {
            const documentData = {
              ano_competencia: momentAddMonthYear,
              mes_competencia: momentAddMonthMonthPlus,
              tipo_documento: 1,
              operadora: doc?.operadora,
              entidade:
                doc?.fl_resp_pelo_pagamento === "E"
                  ? doc?.entidade_vinculada
                  : doc?.entidade,
              contrato: doc?.id,
              dt_vencimento: moment([
                momentAddMonthYear,
                momentAddMonthMonth,
                verificarDiaMes(
                  parseInt(doc?.dia_venc_extrato_boleto_nota),
                  momentAddMonthMonth,
                ),
              ])
                .add(1, "M")
                .format("YYYY-MM-DD"),
              status_documento: 1,
              valor: null,
              fl_envia_pos_limite: false,
              fl_validando_ext_bol_not: false,
            };

            await api
              .post("/documentos", documentData)
              .then(async (res) => {
                // documentsArray.push(res.data);
              })
              .catch((error) => {
                console.log("erro: ", error);
                Swal.fire({
                  icon: "warning",
                  title:
                    "Erro ao criar documentos 1! Entre em contato com o administrador do sistema",
                  showConfirmButton: true,
                  confirmButtonText: `Ok`,
                });
              });
          });

        // GERAR DOCUMENTO PARA COBRANÇA - fl_libera_valid_extbolnot_mes_anterior = FALSE
        data
          .filter((doc: any) => doc.fl_extrato_boleto_nota && doc.fl_ativo)
          .filter(
            (doc: any) =>
              moment([
                doc.ano_ini_vigenc_ext_bol_not,
                doc.mes_ini_vigenc_ext_bol_not - 1,
              ]).format("YYYY/MM") <=
              moment([moment().year(), moment().month()]).format("YYYY/MM"),
          )
          // .filter((doc: any) => !doc.fl_libera_valid_extbolnot_mes_anterior)
          .filter(
            (doc: any) =>
              doc.documentos.find(
                (doc: any) =>
                  doc.mes_competencia === moment().month() + 1 &&
                  doc.ano_competencia === moment().year(),
              ) === undefined,
          )
          .map(async (doc: any) => {
            const documentData = {
              ano_competencia: yearMoment,
              mes_competencia: momentMonthPlus,
              tipo_documento: 1,
              operadora: doc?.operadora,
              entidade:
                doc?.fl_resp_pelo_pagamento === "E"
                  ? doc?.entidade_vinculada
                  : doc?.entidade,
              contrato: doc?.id,
              dt_vencimento: moment([
                yearMoment,
                momentMonthOnly,
                verificarDiaMes(
                  parseInt(doc?.dia_venc_extrato_boleto_nota),
                  momentMonthOnly,
                ),
              ])
                .add(1, "M")
                .format("YYYY-MM-DD"),
              status_documento: 1,
              valor: null,
              fl_envia_pos_limite: false,
              fl_validando_ext_bol_not: false,
            };

            await api
              .post("/documentos", documentData)
              .then(async (res) => {
                // documentsArray.push(res.data);
              })
              .catch((error) => {
                console.log("erro: ", error);
                Swal.fire({
                  icon: "warning",
                  title:
                    "Erro ao criar documentos 2! Entre em contato com o administrador do sistema",
                  showConfirmButton: true,
                  confirmButtonText: `Ok`,
                });
              });
          });
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Erro ao listar os contratos",
        showConfirmButton: true,
        confirmButtonText: `Ok`,
      });
    }
  }

  async function getContratosAllUtilizacao() {
    try {
      let data;

      const response = await api.get(`/contratoes`);
      data = response.data;

      if (data.length > 0) {
        setContratos(data);

        data
          .filter((doc: any) => doc.fl_sinistralidade && doc.fl_ativo)
          .filter(
            (doc: any) =>
              moment([
                doc.ano_ini_vigenc_ext_bol_not,
                doc.mes_ini_vigenc_ext_bol_not - 1,
              ]).format("YYYY/MM") <=
              moment([moment().year(), moment().month()]).format("YYYY/MM"),
          )
          // .filter((doc: any) => !doc.fl_libera_valid_extbolnot_mes_anterior)
          .filter(
            (doc: any) =>
              doc.documentos.find(
                (doc: any) =>
                  doc.tipo_documento === 10 &&
                  doc.mes_competencia === moment().month() + 1 &&
                  doc.ano_competencia === moment().year(),
              ) === undefined,
          )
          .map(async (doc: any) => {
            const documentData = {
              ano_competencia: yearMoment,
              mes_competencia: momentMonthPlus,
              tipo_documento: 10,
              operadora: doc?.operadora,
              entidade: doc?.entidade,
              contrato: doc?.id,
              dt_vencimento: moment([
                yearMoment,
                momentMonthOnly,
                verificarDiaMes(
                  parseInt(doc?.dia_recebimento_arq_sinist),
                  momentMonthOnly,
                ),
              ])
                .add(1, "M")
                .format("YYYY-MM-DD"),
              status_documento: 1,
              valor: null,
              fl_envia_pos_limite: false,
              fl_validando_ext_bol_not: false,
            };

            await api
              .post("/documentos", documentData)
              .then(async (res) => {
                // documentsArray.push(res.data);
              })
              .catch((error) => {
                console.log("erro: ", error);
                Swal.fire({
                  icon: "warning",
                  title:
                    "Erro ao criar documentos de Utilização! Entre em contato com o administrador do sistema",
                  showConfirmButton: true,
                  confirmButtonText: `Ok`,
                });
              });
          });
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Erro ao listar os contratos",
        showConfirmButton: true,
        confirmButtonText: `Ok`,
      });
    }
  }

  async function getContratosAllRepasseTaxas() {
    try {
      let data;

      const response = await api.get(`/contratoes`);
      data = response.data;

      if (data.length > 0) {
        setContratos(data);

        data
          .filter((doc: any) => doc.fl_repasse_taxa && doc.fl_ativo)
          .filter(
            (doc: any) =>
              moment([
                doc.ano_inicio_repasse,
                doc.mes_inicio_repasse - 1,
              ]).format("YYYY/MM") <=
              moment([moment().year(), moment().month()]).format("YYYY/MM"),
          )
          // .filter((doc: any) => !doc.fl_libera_valid_extbolnot_mes_anterior)
          .filter(
            (doc: any) =>
              doc.documentos.find(
                (doc: any) =>
                  doc.tipo_documento === 2 &&
                  doc.mes_competencia === moment().month() + 1 &&
                  doc.ano_competencia === moment().year(),
              ) === undefined,
          )
          .map(async (doc: any) => {
            const documentData = {
              ano_competencia: yearMoment,
              mes_competencia: momentMonthPlus,
              tipo_documento: 2,
              operadora: doc?.operadora,
              entidade: doc?.entidade,
              contrato: doc?.id,
              dt_vencimento: moment([
                yearMoment,
                momentMonthOnly,
                verificarDiaMes(parseInt(doc?.dia_repasse), momentMonthOnly),
              ])
                .add(1, "M")
                .format("YYYY-MM-DD"),
              status_documento: 1,
              valor: null,
              fl_envia_pos_limite: false,
              fl_validando_ext_bol_not: false,
            };

            await api
              .post("/documentos", documentData)
              .then(async (res) => {
                // documentsArray.push(res.data);
              })
              .catch((error) => {
                console.log("erro: ", error);
                Swal.fire({
                  icon: "warning",
                  title:
                    "Erro ao criar documentos Repasse! Entre em contato com o administrador do sistema",
                  showConfirmButton: true,
                  confirmButtonText: `Ok`,
                });
              });
          });
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Erro ao listar os contratos",
        showConfirmButton: true,
        confirmButtonText: `Ok`,
      });
    }
  }

  async function getContratosAllCoparticipacao() {
    try {
      let data;

      const response = await api.get(`/contratoes`);
      data = response.data;

      if (data.length > 0) {
        setContratos(data);

        data
          .filter((doc: any) => doc.fl_copart && doc.fl_ativo)
          .filter(
            (doc: any) =>
              doc.documentos.find(
                (doc: any) =>
                  doc.tipo_documento === 7 &&
                  doc.mes_competencia === moment().month() + 1 &&
                  doc.ano_competencia === moment().year(),
              ) === undefined,
          )
          .map(async (doc: any) => {
            const documentData = {
              ano_competencia: yearMoment,
              mes_competencia: momentMonthPlus,
              tipo_documento: 7,
              operadora: doc?.operadora,
              entidade: doc?.entidade,
              contrato: doc?.id,
              dt_vencimento: moment([
                yearMoment,
                momentMonthOnly,
                verificarDiaMes(
                  parseInt(doc?.dia_venc_copart),
                  momentMonthOnly,
                ),
              ])
                .add(1, "M")
                .format("YYYY-MM-DD"),
              status_documento: 1,
              valor: null,
              fl_envia_pos_limite: false,
              fl_validando_ext_bol_not: false,
            };

            await api
              .post("/documentos", documentData)
              .then(async (res) => {
                // documentsArray.push(res.data);
              })
              .catch((error) => {
                console.log("erro: ", error);
                Swal.fire({
                  icon: "warning",
                  title:
                    "Erro ao criar documento Coparticipação! Entre em contato com o administrador do sistema",
                  showConfirmButton: true,
                  confirmButtonText: `Ok`,
                });
              });
          });
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Erro ao listar os contratos",
        showConfirmButton: true,
        confirmButtonText: `Ok`,
      });
    }
  }

  async function signIn({ username, password }: SignInData) {
    const loginInfo = {
      identifier: username,
      password: password,
    };

    setLoadingLogin(true);

    await api
      .post("/auth/local", loginInfo)
      .then((res: { data: { jwt: string; user: any } }) => {
        const { jwt, user } = res.data;
        localStorage.setItem("@portalinterbrasil:token", jwt);
        api.defaults.headers.Authorization = `Bearer ${jwt}`;

        getContratosAllCobranca();
        getContratosAllRepasseTaxas();
        getContratosAllUtilizacao();
        getContratosAllCoparticipacao();
        getContratos(user.id);
        setUser(user);
        setuserType(user.role.name);
        setSigned(true);
        setLoadingLogin(true);
      })
      .catch((e) => {
        Swal.fire({
          icon: "warning",
          title: "Usuário ou senha inválidos, tente novamente!",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
        setLoadingLogin(false);
      });
  }

  function signOut() {
    setSigned(false);
    setLoadingLogin(false);
    localStorage.removeItem("@portalinterbrasil:token");
    localStorage.removeItem("@portalinterbrasil:user");
    setUser(null);
    window.location.href = "/";
    // window.location.reload();
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        contratos,
        qtdContratosCobranca,
        qtdContratosCoparticipacao,
        qtdContratosRepasseTaxas,
        qtdContratosUtilizacao,
        isAuthenticated,
        signIn,
        signOut,
        setUser,
        userType,
        signed,
        loadingLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};
